/**
 * Identité, langues et rôles. Le socle : ce que tout le produit connaît avant de connaître
 * un cabinet, un patient ou une facture.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

export const countries = ['LU'] as const;
export type Country = (typeof countries)[number];

export const languages = ['fr', 'de', 'en'] as const;
export type Language = (typeof languages)[number];

export const themes = ['light', 'dark', 'system'] as const;
export type Theme = (typeof themes)[number];

/**
 * Teinte d'accent des courriels d'un cabinet, choisie dans cette liste et nulle part ailleurs. Les
 * valeurs hexadécimales vivent côté serveur (`apps/api/src/lib/email-templates/accent.ts`) : le
 * réseau ne transporte qu'un nom, jamais une couleur. Nulle en base = l'apparence d'origine.
 */
export const emailAccentColors = [
  'teal',
  'blue',
  'indigo',
  'violet',
  'rose',
  'amber',
  'green',
  'slate',
] as const;
export type EmailAccentColor = (typeof emailAccentColors)[number];

/** Rôle au niveau plateforme. `user` = praticien ou membre de cabinet ordinaire. */
export const platformRoles = ['user', 'support', 'super_admin'] as const;
export type PlatformRole = (typeof platformRoles)[number];

/**
 * L'équipe de la plateforme : tout rôle autre que `user`. La même liste que `is_platform_staff()`
 * (migration `0050`) et que les `requirePlatformRole('support', 'super_admin')` de la console —
 * une seule notion d'« équipe », tenue ici côté code.
 */
export const platformStaffRoles = [
  'support',
  'super_admin',
] as const satisfies readonly PlatformRole[];

export function isPlatformStaff(role: PlatformRole): boolean {
  return (platformStaffRoles as readonly PlatformRole[]).includes(role);
}

/**
 * Rôle dans un cabinet. Porte une sémantique de facturation et de rétrocession
 * (docs/audit/audit-schema.md §2.4). Simplifiés par Nicolas le 30 septembre 2026 :
 *
 * - `owner` : le propriétaire, **exactement un** par cabinet (index unique en base, migration
 *   `0082`) ; on ne le retire, ne le désactive ni ne le rétrograde : on transfère la propriété.
 * - `collaborateur` et `salarie` : praticiens permanents, offre Cabinet seulement. Le salarié
 *   reverse par défaut 100 % de ses honoraires au titulaire (voir `retrocessionModelByRole`).
 * - `remplacant` : praticien qui n'exerce **que** pendant un remplacement daté
 *   (`member_replacements`), à la place d'un praticien permanent.
 * - `secretary` : jamais praticien — ni rendez-vous, ni consultation, ni facture à son nom.
 *
 * `assistant` a disparu : les assistants sont devenus secrétaires (migration `0082`). La valeur
 * reste dans l'énumération Postgres, inutilisée et interdite par contrainte, parce qu'on ne
 * retire pas une valeur d'un type sans le recréer, et avec lui chaque colonne qui l'emploie.
 */
export const memberRoles = [
  'owner',
  'collaborateur',
  'remplacant',
  'salarie',
  'secretary',
] as const;
export type MemberRole = (typeof memberRoles)[number];

/**
 * Les rôles qui soignent : eux seuls tiennent un agenda, une consultation ou une facture. Liste
 * unique, lue par le serveur (refus `not_a_practitioner`) et par le front (listes de praticiens).
 */
export const practitionerRoles = [
  'owner',
  'collaborateur',
  'salarie',
  'remplacant',
] as const satisfies readonly MemberRole[];
export type PractitionerRole = (typeof practitionerRoles)[number];

export function isPractitionerRole(role: MemberRole): role is PractitionerRole {
  return (practitionerRoles as readonly MemberRole[]).includes(role);
}

/**
 * Les rôles qu'on donne à quelqu'un : à l'invitation, au changement de rôle, à l'ancien
 * propriétaire après un transfert. `owner` n'en est pas — la propriété se **transfère**.
 */
export const assignableMemberRoles = [
  'collaborateur',
  'salarie',
  'remplacant',
  'secretary',
] as const satisfies readonly MemberRole[];
export type AssignableMemberRole = (typeof assignableMemberRoles)[number];

/** Ceux qu'un remplaçant peut remplacer : les praticiens permanents, propriétaire compris. */
export const replaceableRoles = [
  'owner',
  'collaborateur',
  'salarie',
] as const satisfies readonly MemberRole[];
export type ReplaceableRole = (typeof replaceableRoles)[number];

export function isReplaceableRole(role: MemberRole): role is ReplaceableRole {
  return (replaceableRoles as readonly MemberRole[]).includes(role);
}

/**
 * Jetons à usage unique. `email_change` porte la **nouvelle** adresse dans sa charge utile : tant
 * qu'elle n'est pas confirmée depuis la boîte visée, le compte garde l'ancienne.
 */
export const authTokenKinds = [
  'password_reset',
  'email_verification',
  'email_change',
  'invitation',
] as const;
export type AuthTokenKind = (typeof authTokenKinds)[number];
